import {
  Box,
  Drawer,
  Typography,
  Chip,
  Link,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  capitalizeFirstLetter,
  formatStat,
  getRateRange,
  getStringHash,
  parseFollowers,
} from "../util/helpers";
import {
  IconSparkles,
  IconX,
  IconUsers,
  IconTrendingUp,
  IconCash,
  IconWorld,
  IconEye,
  IconHeart,
  IconMail,
  IconFileDescription,
  IconPhoto,
} from "@tabler/icons-react";
import { memo } from "react";
import {
  InstagramEmbed,
  TikTokEmbed,
  YouTubeEmbed,
  LinkedInEmbed,
} from "react-social-media-embed";
import { Button } from "../components";

const StatCard = ({ icon, label, value, subtitle }) => (
  <Box
    sx={{
      flex: "1 1 calc(33.33% - 8px)",
      background: "var(--neutral-50)",
      borderRadius: "12px",
      padding: "16px 12px",
      textAlign: "center",
      border: "1px solid var(--neutral-200)",
    }}
  >
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{ mb: 0.5, color: "var(--secondary-500)" }}
    >
      {icon}
    </Box>
    <Typography
      variant="caption"
      sx={{
        color: "var(--body-text-color-500)",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontSize: "0.65rem",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, color: "var(--main-text-color)", mt: 0.25 }}
    >
      {value}
    </Typography>
    {subtitle && (
      <Typography
        variant="caption"
        sx={{ color: "var(--body-text-color-200)", fontSize: "0.7rem" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

function toLinkedInEmbedUrl(postUrl) {
  if (postUrl.includes("/embed/")) return postUrl;
  if (postUrl.includes("/feed/update/")) {
    return postUrl.replace("/feed/update/", "/embed/feed/update/");
  }
  const activityMatch = postUrl.match(/activity[- ](\d+)/);
  if (activityMatch) {
    return `https://www.linkedin.com/embed/feed/update/urn:li:activity:${activityMatch[1]}`;
  }
  return null;
}

function OutreachDrawer({ open, onClose, creator }) {
  if (!creator) return null;

  const hasOrganicStats =
    formatStat(creator.reach) !== 0 ||
    formatStat(creator.estimatedViews) !== 0 ||
    formatStat(creator.estimatedEngagement) !== 0;
  const engagementRate =
    creator.estimatedViews > 0 && creator.estimatedEngagement != null
      ? Math.round(
          (creator.estimatedEngagement / creator.estimatedViews) * 100 * 100
        ) / 100
      : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      style={{ zIndex: 100000 }}
      PaperProps={{
        sx: {
          background: "#FAFAFE",
          borderLeft: "1px solid var(--neutral-200)",
        },
      }}
    >
      <Box sx={{ minWidth: 340, width: "32vw", maxWidth: 520 }}>
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background:
              "linear-gradient(135deg, rgba(159,37,246,0.04) 0%, rgba(92,164,251,0.04) 100%)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid var(--neutral-200)",
            px: 3,
            py: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <IconSparkles size={18} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  background: "var(--color-gradient)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.3px",
                }}
              >
                CREATOR OUTREACH
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "var(--body-text-color-500)" }}
            >
              <IconX size={18} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 3, py: 2.5 }}>
          {/* Profile Card */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: "16px",
              border: "1px solid var(--neutral-200)",
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <Box display="flex" gap={2} alignItems="center">
              <Avatar
                src={
                  creator?.profileImage
                    ? creator.profileImage
                    : `../gradient${getStringHash(creator?.name, 10)}.png`
                }
                alt={creator.name}
                sx={{
                  width: 64,
                  height: 64,
                  border: "3px solid var(--neutral-100)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              />
              <Box flex={1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "var(--main-text-color)",
                    lineHeight: 1.3,
                  }}
                >
                  {creator?.name}
                </Typography>
                <Link
                  href={
                    creator?.tiktok ||
                    creator?.youtube ||
                    creator?.linkedin ||
                    creator?.instagram
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: "none",
                    color: "var(--secondary-600)",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {(creator?.handle !== "-" && creator?.handle)
                    ? `@${creator.handle}`
                    : creator?.email}
                </Link>
                <Box display="flex" gap={0.75} mt={1} flexWrap="wrap">
                  {creator?.industry
                    ?.split(",")
                    .filter(Boolean)
                    .map((industry) => (
                      <Chip
                        key={industry}
                        label={capitalizeFirstLetter(industry.trim())}
                        size="small"
                        sx={{
                          backgroundColor: "var(--neutral-100)",
                          color: "var(--main-text-color)",
                          fontWeight: 500,
                          fontSize: "0.7rem",
                        }}
                      />
                    ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Summary */}
          <Box sx={{ mb: 2.5 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <IconFileDescription size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "var(--main-text-color)",
                  letterSpacing: "0.2px",
                }}
              >
                Summary
              </Typography>
            </Box>
            <Box
              sx={{
                background: "#fff",
                borderRadius: "12px",
                border: "1px solid var(--neutral-200)",
                p: 2.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "var(--body-text-color-600)",
                  lineHeight: 1.6,
                }}
              >
                This creator has built a great community with over{" "}
                {creator.followers} followers. People follow this creator for{" "}
                {creator.industry} and more topics. They are currently located
                in {creator.location}, and are relevant for {creator.relevance}{" "}
                campaigns. This creator can collaborate on paid, affiliate,
                UGC, pr, events campaigns and more. This creator is within
                the {creator?.ageBracket} age bracket. Audience Tier is{" "}
                {creator.audience_tier}.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "var(--body-text-color-500)",
                  lineHeight: 1.6,
                  mt: 1.5,
                  fontStyle: "italic",
                }}
              >
                Want to partner with this creator? Launch a campaign and invite
                them to partner, or send them a custom email below.
              </Typography>
            </Box>
          </Box>

          {/* Key metrics */}
          <Box display="flex" gap="8px" flexWrap="wrap" sx={{ mb: 2.5 }}>
            <StatCard
              icon={<IconUsers size={18} />}
              label="Followers"
              value={creator.followers ?? "—"}
            />
            <StatCard
              icon={<IconTrendingUp size={18} />}
              label="Relevance"
              value={creator.relevance ?? "—"}
            />
            <StatCard
              icon={<IconCash size={18} />}
              label="Est. Cost"
              value={`${getRateRange(parseFollowers(creator.followers))}/post`}
            />
            {creator.country && (
              <StatCard
                icon={<IconWorld size={18} />}
                label="Country"
                value={
                  Array.isArray(creator?.location)
                    ? creator.location.join(", ")
                    : creator?.location ?? creator.country
                }
              />
            )}
            {creator.city && (
              <StatCard
                icon={<IconWorld size={18} />}
                label="City"
                value={creator.city}
              />
            )}
            {hasOrganicStats && (formatStat(creator.reach) !== 0 || formatStat(creator.estimatedViews) !== 0) && (
              <StatCard
                icon={<IconEye size={18} />}
                label="Est. Organic Views"
                value={`${formatStat(creator.reach) || formatStat(creator.estimatedViews)}/post`}
              />
            )}
            {formatStat(creator.estimatedEngagement) !== 0 && (
              <StatCard
                icon={<IconHeart size={18} />}
                label="Est. Organic Engagement"
                value={formatStat(creator.estimatedEngagement)}
              />
            )}
            {engagementRate != null && (
              <StatCard
                icon={<IconTrendingUp size={18} />}
                label="Est. Engagement Rate"
                value={`${engagementRate}%`}
              />
            )}
            {creator.audience_tier && (
              <StatCard
                icon={<IconUsers size={18} />}
                label="Audience Tier"
                value={creator.audience_tier}
              />
            )}
          </Box>

          {/* Latest Posts */}
          {creator?.latestPosts?.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <IconPhoto size={16} color="var(--secondary-500)" />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "var(--main-text-color)",
                    letterSpacing: "0.2px",
                  }}
                >
                  Latest Posts
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {creator.latestPosts.slice(0, 3).map((postUrl, index) => {
                  const isInstagram = postUrl.includes("instagram.com");
                  const isTikTok = postUrl.includes("tiktok.com");
                  const isYouTube = postUrl.includes("youtube.com") || postUrl.includes("youtu.be");
                  const isLinkedIn = postUrl.includes("linkedin.com");

                  return (
                    <Box
                      key={index}
                      sx={{
                        background: "#fff",
                        borderRadius: "12px",
                        border: "1px solid var(--neutral-200)",
                        overflow: "hidden",
                        "& iframe": { borderRadius: "8px" },
                      }}
                    >
                      {isInstagram && (
                        <InstagramEmbed url={postUrl} width="100%" captioned />
                      )}
                      {isTikTok && (
                        <TikTokEmbed url={postUrl} width="100%" />
                      )}
                      {isYouTube && (
                        <YouTubeEmbed url={postUrl} width="100%" />
                      )}
                      {isLinkedIn && toLinkedInEmbedUrl(postUrl) && (
                        <LinkedInEmbed url={toLinkedInEmbedUrl(postUrl)} postUrl={postUrl} width="100%" />
                      )}
                      {isLinkedIn && !toLinkedInEmbedUrl(postUrl) && (
                        <Box sx={{ p: 2 }}>
                          <Link
                            href={postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: "0.85rem", wordBreak: "break-all" }}
                          >
                            {postUrl}
                          </Link>
                        </Box>
                      )}
                      {!isInstagram && !isTikTok && !isYouTube && !isLinkedIn && (
                        <Box sx={{ p: 2 }}>
                          <Link
                            href={postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: "0.85rem", wordBreak: "break-all" }}
                          >
                            {postUrl}
                          </Link>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Footnote */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "var(--body-text-color-500)",
              mb: 2.5,
              lineHeight: 1.5,
            }}
          >
            *Average performance of sponsored post views and engagement may range
            ~20-30% lower than organic.
          </Typography>

          {/* Send a Custom Email */}
          <Box
            sx={{
              background: "#fff",
              borderRadius: "12px",
              border: "1px solid var(--neutral-200)",
              p: 2.5,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <IconMail size={16} color="var(--secondary-500)" />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "var(--main-text-color)",
                  letterSpacing: "0.2px",
                }}
              >
                Send a Custom Email
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "var(--body-text-color-500)",
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              You can invite this creator to apply for your campaign by sending
              them a customized email with your campaign link. If you would like
              to use a Gigl templated email, click the Outreach button (available
              in the outreach tab on your campaign page).
            </Typography>
            <Button
              gradient
              style={{ borderRadius: 10, width: "100%" }}
              href={`mailto:${creator.email}?subject=Gigl: New Campaign Partnership Opportunity`}
            >
              Send a Custom Email
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default memo(OutreachDrawer);
